import React, { useEffect, useRef, useState } from "react";
import { Accordion, Button, Card, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import moment from "moment";
import "./WeekHourGrid.css";

const SLOT_MINUTES = 15;
const SLOTS_PER_DAY = 24 * 4;

const GRID_ROWS = Array.from({ length: SLOTS_PER_DAY }, (_, slot) => ({
  slot,
  hour: Math.floor(slot / 4),
  quarter: slot % 4,
}));

export function minutesFromClock(time) {
  if (!time) return null;
  if (time === "24:00") return 24 * 60;
  const [hStr, mStr = "0"] = String(time).split(":");
  const hours = parseInt(hStr, 10);
  const minutes = parseInt(mStr, 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

export function entryRanges(entry) {
  if (!entry) return [];
  const ranges = [];
  const push = (start, end) => {
    const rangeStart = minutesFromClock(start);
    const rangeEnd = minutesFromClock(end);
    if (rangeStart == null || rangeEnd == null || rangeEnd <= rangeStart) return;
    ranges.push([rangeStart, rangeEnd]);
  };
  push(entry.amStart, entry.amEnd);
  push(entry.pmStart, entry.pmEnd);
  return ranges;
}

export function applyMergedSelection(entry, startMinutes, endMinutes) {
  const existing = entryRanges(entry);
  const kept = [];
  let merged = null;
  existing.forEach(([rangeStart, rangeEnd]) => {
    const touches = startMinutes <= rangeEnd && endMinutes >= rangeStart;
    if (!touches) {
      kept.push([rangeStart, rangeEnd]);
      return;
    }
    const nextStart = Math.min(rangeStart, startMinutes, merged ? merged[0] : Infinity);
    const nextEnd = Math.max(rangeEnd, endMinutes, merged ? merged[1] : -Infinity);
    merged = [nextStart, nextEnd];
  });

  const combined = merged ? [...kept, merged] : [[startMinutes, endMinutes]];
  combined.sort((a, b) => a[0] - b[0]);
  const collapsed = [];
  combined.forEach(([rangeStart, rangeEnd]) => {
    const last = collapsed[collapsed.length - 1];
    if (last && rangeStart <= last[1]) {
      last[1] = Math.max(last[1], rangeEnd);
    } else {
      collapsed.push([rangeStart, rangeEnd]);
    }
  });

  const am = collapsed[0] || [startMinutes, endMinutes];
  const pm = collapsed[1] || null;
  return {
    ranges: collapsed,
    amStart: am[0],
    amEnd: am[1],
    pmStart: pm ? pm[0] : null,
    pmEnd: pm ? pm[1] : null,
  };
}

function contiguousRuns(ranges) {
  const runs = [];
  let runStart = null;
  let runEnd = null;
  for (let slot = 0; slot < SLOTS_PER_DAY; slot += 1) {
    if (slotCovered(slot, ranges)) {
      if (runStart == null) runStart = slot;
      runEnd = slot;
    } else if (runStart != null) {
      runs.push({ slot: runStart, span: runEnd - runStart + 1 });
      runStart = null;
      runEnd = null;
    }
  }
  if (runStart != null) {
    runs.push({ slot: runStart, span: runEnd - runStart + 1 });
  }
  return runs;
}

function slotCovered(slot, ranges) {
  const start = slot * SLOT_MINUTES;
  const end = start + SLOT_MINUTES;
  return ranges.some(([rangeStart, rangeEnd]) => start < rangeEnd && end > rangeStart);
}

function normalizeDrag(drag) {
  return {
    day0: Math.min(drag.anchor.day, drag.current.day),
    day1: Math.max(drag.anchor.day, drag.current.day),
    slot0: Math.min(drag.anchor.slot, drag.current.slot),
    slot1: Math.max(drag.anchor.slot, drag.current.slot),
    erase: drag.erase,
  };
}

function cellFromPoint(x, y) {
  const el = document.elementFromPoint(x, y);
  const cell = el?.closest?.("[data-day][data-slot]");
  if (!cell) return null;
  const day = Number(cell.dataset.day);
  const slot = Number(cell.dataset.slot);
  if (Number.isNaN(day) || Number.isNaN(slot)) return null;
  return { day, slot };
}

export function WeekHourGrid({
  weekDates,
  logData,
  isDayDisabled,
  blockedTypeFor,
  onCommit,
}) {
  const dragRef = useRef(null);
  const scrollRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const dateAt = (dayIndex) => weekDates[dayIndex]?.format("YYYY-MM-DD");

  const rangesAt = (dayIndex) => entryRanges(logData[dateAt(dayIndex)]);

  const setDragState = (next) => {
    dragRef.current = next;
    setDrag(next);
  };

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;
    const frame = requestAnimationFrame(() => {
      const header = scroller.querySelector(".week-hour-grid-header");
      const sixAm = scroller.querySelector('.week-hour-row[data-slot="24"]');
      const sixPm = scroller.querySelector('.week-hour-row[data-slot="72"]');
      if (!header || !sixAm || !sixPm) return;
      const contentTop = (el) =>
        el.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      const start = contentTop(sixAm);
      const end = contentTop(sixPm);
      const headerHeight = header.getBoundingClientRect().height;
      const span = end - start;
      const visibleBody = scroller.clientHeight - headerHeight;
      const scrollTop = start - headerHeight - (visibleBody - span) / 2;
      scroller.scrollTop = Math.max(0, scrollTop);
    });
    return () => cancelAnimationFrame(frame);
  }, [weekDates]);

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    const hit = cellFromPoint(event.clientX, event.clientY);
    if (!hit) return;
    const date = dateAt(hit.day);
    if (!date || isDayDisabled(date)) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragState({ anchor: hit, current: hit });
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const hit = cellFromPoint(event.clientX, event.clientY);
    if (!hit) return;
    if (
      hit.day === dragRef.current.current.day &&
      hit.slot === dragRef.current.current.slot
    ) {
      return;
    }
    setDragState({ ...dragRef.current, current: hit });
  };

  const finishDrag = (event) => {
    const current = dragRef.current;
    if (!current) return;
    dragRef.current = null;
    setDrag(null);
    if (event?.detail >= 2) return;
    const box = normalizeDrag(current);
    const dateStrings = [];
    for (let day = box.day0; day <= box.day1; day += 1) {
      const date = dateAt(day);
      if (date) dateStrings.push(date);
    }
    if (dateStrings.length === 0) return;
    onCommit({
      dateStrings,
      startMinutes: box.slot0 * SLOT_MINUTES,
      endMinutes: (box.slot1 + 1) * SLOT_MINUTES,
    });
  };

  const onDoubleClick = (event) => {
    const hit = cellFromPoint(event.clientX, event.clientY);
    if (!hit) return;
    const date = dateAt(hit.day);
    if (!date || isDayDisabled(date)) return;
    if (!slotCovered(hit.slot, rangesAt(hit.day))) return;
    event.preventDefault();
    onCommit({
      dateStrings: [date],
      startMinutes: hit.slot * SLOT_MINUTES,
      endMinutes: (hit.slot + 1) * SLOT_MINUTES,
      clear: true,
    });
  };

  const preview = drag ? normalizeDrag(drag) : null;
  const previewStart = preview ? preview.slot0 * SLOT_MINUTES : 0;
  const previewEnd = preview ? (preview.slot1 + 1) * SLOT_MINUTES : 0;

  const blocksByDay = weekDates.map((_, dayIndex) => {
    const date = dateAt(dayIndex);
    const disabled = !date || isDayDisabled(date);
    const inDrag =
      preview &&
      !disabled &&
      dayIndex >= preview.day0 &&
      dayIndex <= preview.day1;
    const ranges = inDrag
      ? applyMergedSelection(logData[date], previewStart, previewEnd).ranges
      : rangesAt(dayIndex);
    return contiguousRuns(ranges).map((run) => ({ ...run, draft: inDrag }));
  });

  return (
    <div
      className="week-hour-grid-scroll"
      ref={scrollRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onDoubleClick={onDoubleClick}
    >
      <div className="week-hour-grid-header" role="row">
        <div className="week-hour-gutter" />
        {weekDates.map((day) => {
          const date = day.format("YYYY-MM-DD");
          const blocked = blockedTypeFor(date);
          const isToday = day.isSame(moment(), "day");
          return (
            <div
              key={date}
              className={`week-hour-dayhead${isToday ? " is-today" : ""}`}
            >
              <div className="week-hour-dayname">{day.format("ddd")}</div>
              <div className="week-hour-daynum">{day.format("D")}</div>
              {blocked && (
                <span className="badge bg-secondary mt-1">{blocked}</span>
              )}
            </div>
          );
        })}
      </div>
      {GRID_ROWS.map((row) => (
        <div
          key={row.slot}
          className={`week-hour-row quarter-${row.quarter}`}
          data-slot={row.slot}
          role="row"
        >
          <div className="week-hour-gutter">
            {row.quarter === 0
              ? moment().startOf("day").add(row.hour, "hours").format("h:mm A")
              : ""}
          </div>
          {weekDates.map((day, dayIndex) => {
            const date = day.format("YYYY-MM-DD");
            const disabled = isDayDisabled(date);
            const block = blocksByDay[dayIndex]?.find((run) => run.slot === row.slot);
            const className = [
              "week-hour-cell",
              disabled ? "is-disabled" : "",
              block ? "has-block" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <div
                key={date}
                className={className}
                data-day={dayIndex}
                data-slot={row.slot}
                role="gridcell"
              >
                {block && (
                  <div
                    className={`week-hour-block${block.draft ? " is-draft" : ""}`}
                    style={{ height: `calc(${block.span} * 100%)` }}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function formatMinutes(total) {
  const safe = Math.max(0, total || 0);
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function timeInputValue(value) {
  if (!value) return "";
  if (value === "24:00") return "23:59";
  return value;
}

export function WeekHourOverview({
  weekDates,
  logData,
  comments,
  onCommentChange,
  onTimeChange,
  onWeekCategoryChange,
  isDayDisabled,
  blockedTypeFor,
  minutesForEntry,
  rotationOptions = [],
  weekTotal,
  onSave,
  saving,
  saveDisabled,
  locked,
  extraActions,
}) {
  const [openDay, setOpenDay] = useState(null);
  const todayKey =
    weekDates[0]?.format("YYYY-MM-DD") || "0";

  useEffect(() => {
    setOpenDay(null);
  }, [todayKey]);

  const weekRange =
    weekDates.length > 0
      ? `${weekDates[0].format("MMM D")} – ${weekDates[weekDates.length - 1].format("MMM D")}`
      : "";

  const editableDates = weekDates
    .map((day) => day.format("YYYY-MM-DD"))
    .filter((date) => !isDayDisabled(date));
  const weekRotations = editableDates.map((date) => logData[date]?.rotation || "");
  const weekCategory =
    weekRotations.length > 0 && weekRotations.every((name) => name === weekRotations[0])
      ? weekRotations[0]
      : "";
  const weekCategoryNames =
    weekCategory && !rotationOptions.includes(weekCategory)
      ? [weekCategory, ...rotationOptions]
      : rotationOptions;

  return (
    <Card className="border-0 shadow-sm mb-3 week-hour-overview">
      <Card.Header
        className="border-0 text-white px-0"
        style={{
          background:
            "linear-gradient(45deg, var(--blue) 0%, #252F4A 60%, var(--green) 100%)",
          height: "70px",
        }}
      >
        <div className="d-flex align-items-center gap-2 px-3">
          <div>
            <div className="smaller text-white-75">Week Overview</div>
            <div className="fw-semibold">{weekRange}</div>
          </div>
        </div>
        <div
          className="w-100 px-0 mt-2 d-flex align-items-center justify-content-center"
          style={{
            background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
            height: "12px",
          }}
        />
      </Card.Header>
      <Card.Body className="p-3">
      <div className="mb-3">
        <FloatingLabel controlId="week-category" label="Category">
          <Form.Select
            value={weekCategory}
            disabled={editableDates.length === 0}
            onChange={(event) => onWeekCategoryChange(event.target.value)}
          >
            <option value="">Select rotation</option>
            {weekCategoryNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>
        </FloatingLabel>
      </div>
      <Accordion activeKey={openDay} onSelect={(key) => setOpenDay(key)}>
        {weekDates.map((day) => {
          const date = day.format("YYYY-MM-DD");
          const entry = logData[date] || {};
          const blocked = blockedTypeFor(date);
          const disabled = isDayDisabled(date);
          const totalLabel = blocked
            ? "--"
            : formatMinutes(minutesForEntry(entry));
          const categoryNames =
            entry.rotation && !rotationOptions.includes(entry.rotation)
              ? [entry.rotation, ...rotationOptions]
              : rotationOptions;
          return (
            <Accordion.Item eventKey={date} key={date}>
              <Accordion.Header>
                <span className="d-flex flex-grow-1 justify-content-between align-items-center">
                  <span>{day.format("dddd")}</span>
                  <span className="fw-bold week-hour-day-total">{totalLabel}</span>
                </span>
              </Accordion.Header>
              <Accordion.Body>
                {blocked ? (
                  <div className="text-muted small">{blocked} leave</div>
                ) : (
                  <>
                    <Row className="g-2">
                      <Col xs={6}>
                        <FloatingLabel controlId={`start-${date}`} label="Start">
                          <Form.Control
                            type="time"
                            value={timeInputValue(entry.amStart)}
                            disabled={disabled}
                            placeholder="Start"
                            onChange={(event) =>
                              onTimeChange(date, "amStart", event.target.value)
                            }
                          />
                        </FloatingLabel>
                      </Col>
                      <Col xs={6}>
                        <FloatingLabel controlId={`end-${date}`} label="End">
                          <Form.Control
                            type="time"
                            value={timeInputValue(entry.amEnd)}
                            disabled={disabled}
                            placeholder="End"
                            onChange={(event) =>
                              onTimeChange(date, "amEnd", event.target.value)
                            }
                          />
                        </FloatingLabel>
                      </Col>
                    </Row>
                    {entry.amEnd === "24:00" && (
                      <div className="form-text">Ends at midnight</div>
                    )}
                    <FloatingLabel
                      controlId={`category-${date}`}
                      label="Category"
                      className="mt-2"
                    >
                      <Form.Select
                        value={entry.rotation || ""}
                        disabled={disabled}
                        onChange={(event) =>
                          onTimeChange(date, "rotation", event.target.value)
                        }
                      >
                        <option value="">Select rotation</option>
                        {categoryNames.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                    <FloatingLabel
                      controlId={`comment-${date}`}
                      label="Comment"
                      className="mt-2"
                    >
                      <Form.Control
                        as="textarea"
                        placeholder="Comment"
                        style={{ height: "4.5rem" }}
                        value={comments[date] || ""}
                        disabled={disabled}
                        onChange={(event) => onCommentChange(date, event.target.value)}
                      />
                    </FloatingLabel>
                  </>
                )}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
      <div className="d-flex justify-content-between align-items-center py-2">
        <span>Total</span>
        <span className="fw-bold">{weekTotal}</span>
      </div>
      <div className="d-grid gap-2">
        <Button
          type="button"
          variant="primary"
          disabled={saveDisabled || saving}
          onClick={onSave}
        >
          {saving ? "Saving..." : "Save weekly hour log"}
        </Button>
        {extraActions}
      </div>
      {locked && (
        <div className="alert border-0 alert-success rounded-3 mb-0 mt-3">
          <div className="fw-semibold mb-1">Timesheet Submitted</div>
          <div className="small text-muted">
            Your timesheet has been submitted. If you need to make corrections,
            request an unlock for further modifications.
          </div>
        </div>
      )}
      </Card.Body>
    </Card>
  );
}
